import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from 'components/ui/Sidebar';
import Icon from 'components/AppIcon';
import { useAuth } from 'contexts/AuthContext';
import { supabase } from 'lib/supabase';
import { ROLES } from 'utils/rbac';
import RateTable from './components/RateTable';
import RateOverrideTable from './components/RateOverrideTable';
import RateHistoryPanel from './components/RateHistoryPanel';
import SetRateModal from './components/SetRateModal';
import OverrideModal from './components/OverrideModal';

const LawyerRateManagement = () => {
  const { user, profile } = useAuth();
  const [activeTab, setActiveTab] = useState('rates');
  const [lawyerRates, setLawyerRates] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [rateHistory, setRateHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSetRateModal, setShowSetRateModal] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [selectedLawyerId, setSelectedLawyerId] = useState(null);
  const [lawyers, setLawyers] = useState([]);

  const isAdminOrPartner = [ROLES?.PARTNER, ROLES?.ADMIN]?.includes(profile?.role);

  const fetchLawyers = useCallback(async () => {
    if (!isAdminOrPartner) return;
    try {
      const { data, error: err } = await supabase?.from('user_profiles')?.select('id, full_name, email, role')?.in('role', ['lawyer', 'partner'])?.order('full_name');
      if (!err) setLawyers(data || []);
    } catch (e) {}
  }, [isAdminOrPartner]);

  const fetchRates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      let query = supabase?.from('lawyer_rates')?.select('*, user_profiles!lawyer_rates_lawyer_id_fkey(full_name, email)')?.order('effective_date', { ascending: false });

      if (!isAdminOrPartner) {
        query = query?.eq('lawyer_id', user?.id);
      }

      const { data, error: rateError } = await query;
      if (rateError) throw rateError;

      // Current rates: latest approved rate per lawyer
      const currentRatesMap = {};
      const historyList = [];
      (data || [])?.forEach(r => {
        historyList?.push(r);
        if (!currentRatesMap?.[r?.lawyer_id] && r?.approval_status === 'approved') {
          currentRatesMap[r.lawyer_id] = r;
        }
      });
      setLawyerRates(Object.values(currentRatesMap));
      setRateHistory(historyList);
    } catch (err) {
      setError(err?.message || 'Failed to load rates');
    } finally {
      setLoading(false);
    }
  }, [user?.id, isAdminOrPartner]);

  const fetchOverrides = useCallback(async () => {
    try {
      let query = supabase?.from('case_rate_overrides')?.select('*, user_profiles!case_rate_overrides_lawyer_id_fkey(full_name, email)')?.order('created_at', { ascending: false });

      if (!isAdminOrPartner) {
        query = query?.eq('lawyer_id', user?.id);
      }

      const { data, error: overrideError } = await query;
      if (overrideError) throw overrideError;
      setOverrides(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load overrides');
    }
  }, [user?.id, isAdminOrPartner]);

  useEffect(() => {
    fetchLawyers();
    fetchRates();
    fetchOverrides();
  }, [fetchLawyers, fetchRates, fetchOverrides]);

  const handleSetRate = async (rateData) => {
    try {
      const { error: insertError } = await supabase?.from('lawyer_rates')?.insert({
        lawyer_id: rateData?.lawyerId || user?.id,
        base_hourly_rate: rateData?.baseHourlyRate,
        currency: 'KES',
        effective_date: rateData?.effectiveDate,
        notes: rateData?.notes,
        approval_status: isAdminOrPartner ? 'approved' : 'pending'
      });
      if (insertError) throw insertError;
      setSuccessMessage('Rate set successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setShowSetRateModal(false);
      fetchRates();
    } catch (err) {
      setError(err?.message || 'Failed to set rate');
    }
  };

  const handleApproveOverride = async (overrideId, status) => {
    try {
      const { error: updateError } = await supabase?.from('case_rate_overrides')?.update({
          approval_status: status,
          approved_by: user?.id,
          approved_at: new Date()?.toISOString()
        })?.eq('id', overrideId);
      if (updateError) throw updateError;
      setSuccessMessage(`Override ${status}!`);
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchOverrides();
    } catch (err) {
      setError(err?.message || 'Failed to update override');
    }
  };

  const handleCreateOverride = async (overrideData) => {
    try {
      const { error: insertError } = await supabase?.from('case_rate_overrides')?.insert({
        lawyer_id: overrideData?.lawyerId || user?.id,
        case_id: overrideData?.caseId,
        case_name: overrideData?.caseName,
        standard_rate: overrideData?.standardRate,
        override_rate: overrideData?.overrideRate,
        justification: overrideData?.justification,
        effective_date: overrideData?.effectiveDate,
        approval_status: 'pending'
      });
      if (insertError) throw insertError;
      setSuccessMessage('Override request submitted!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setShowOverrideModal(false);
      fetchOverrides();
    } catch (err) {
      setError(err?.message || 'Failed to create override');
    }
  };

  const tabs = [
    { id: 'rates', label: 'Current Rates', icon: 'DollarSign', count: lawyerRates?.length },
    { id: 'overrides', label: 'Case Overrides', icon: 'Sliders', count: overrides?.length },
    { id: 'history', label: 'Rate History', icon: 'History', count: rateHistory?.length }
  ];

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <Sidebar />
      <main className="flex-1 overflow-y-auto md:ml-sidebar">
        {/* Header */}
        <div className="bg-surface border-b border-border px-6 py-4 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-heading-semibold text-text-primary">Lawyer Rate Management</h1>
              <p className="text-sm text-text-secondary mt-0.5">Manage hourly rates, case overrides, and rate history</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowOverrideModal(true)}
                className="flex items-center gap-2 border border-border text-text-primary px-4 py-2 rounded-lg text-sm font-body-medium hover:bg-secondary-50 transition-colors"
              >
                <Icon name="Sliders" size={16} />
                <span className="hidden sm:inline">Case Override</span>
              </button>
              {isAdminOrPartner && (
                <button
                  onClick={() => setShowSetRateModal(true)}
                  className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm font-body-medium hover:bg-primary-700 transition-colors"
                >
                  <Icon name="Plus" size={16} />
                  <span className="hidden sm:inline">Set Rate</span>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Messages */}
          {successMessage && (
            <div className="mb-4 flex items-center gap-2 bg-success-50 text-success-700 border border-success-200 px-4 py-3 rounded-lg">
              <Icon name="CheckCircle" size={16} />
              <span className="text-sm">{successMessage}</span>
            </div>
          )}
          {error && (
            <div className="mb-4 flex items-center gap-2 bg-error-50 text-error-700 border border-error-200 px-4 py-3 rounded-lg">
              <Icon name="AlertCircle" size={16} />
              <span className="text-sm">{error}</span>
              <button onClick={() => setError(null)} className="ml-auto"><Icon name="X" size={14} /></button>
            </div>
          )}

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-surface rounded-xl border border-border p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-text-secondary">Active Rates</span>
                <div className="w-8 h-8 bg-primary-50 rounded-lg flex items-center justify-center">
                  <Icon name="Users" size={16} className="text-primary" />
                </div>
              </div>
              <p className="text-2xl font-heading-semibold text-text-primary">{lawyerRates?.length}</p>
            </div>
            <div className="bg-surface rounded-xl border border-border p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-text-secondary">Pending Overrides</span>
                <div className="w-8 h-8 bg-warning-50 rounded-lg flex items-center justify-center">
                  <Icon name="Clock" size={16} className="text-warning-600" />
                </div>
              </div>
              <p className="text-2xl font-heading-semibold text-warning-700">
                {overrides?.filter(o => o?.approval_status === 'pending')?.length}
              </p>
            </div>
            <div className="bg-surface rounded-xl border border-border p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-text-secondary">Avg. Base Rate</span>
                <div className="w-8 h-8 bg-success-50 rounded-lg flex items-center justify-center">
                  <Icon name="TrendingUp" size={16} className="text-success-600" />
                </div>
              </div>
              <p className="text-2xl font-heading-semibold text-success-700">
                {lawyerRates?.length > 0
                  ? `KES ${Math.round(lawyerRates?.reduce((s, r) => s + Number(r?.base_hourly_rate), 0) / lawyerRates?.length)?.toLocaleString()}`
                  : '—'
                }
              </p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-secondary-50 p-1 rounded-xl mb-6 w-fit">
            {tabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-body-medium transition-colors ${
                  activeTab === tab?.id
                    ? 'bg-surface text-primary shadow-sm'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                <Icon name={tab?.icon} size={15} />
                {tab?.label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  activeTab === tab?.id ? 'bg-primary-50 text-primary' : 'bg-secondary-200 text-text-secondary'
                }`}>{tab?.count}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="flex items-center gap-3 text-text-secondary">
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                Loading...
              </div>
            </div>
          ) : (
            <>
              {activeTab === 'rates' && (
                <RateTable
                  rates={lawyerRates}
                  isAdminOrPartner={isAdminOrPartner}
                  onSetRate={(lawyerId) => { setSelectedLawyerId(lawyerId); setShowSetRateModal(true); }}
                />
              )}
              {activeTab === 'overrides' && (
                <RateOverrideTable
                  overrides={overrides}
                  isAdminOrPartner={isAdminOrPartner}
                  onApprove={handleApproveOverride}
                  onReject={(id) => handleApproveOverride(id, 'rejected')}
                />
              )}
              {activeTab === 'history' && (
                <RateHistoryPanel history={rateHistory} isAdminOrPartner={isAdminOrPartner} />
              )}
            </>
          )}
        </div>
      </main>
      {/* Modals */}
      {showSetRateModal && (
        <SetRateModal
          lawyers={lawyers}
          selectedLawyerId={selectedLawyerId}
          isAdminOrPartner={isAdminOrPartner}
          currentUserId={user?.id}
          onSave={handleSetRate}
          onClose={() => { setShowSetRateModal(false); setSelectedLawyerId(null); }}
        />
      )}
      {showOverrideModal && (
        <OverrideModal
          lawyers={lawyers}
          isAdminOrPartner={isAdminOrPartner}
          currentUserId={user?.id}
          currentRate={lawyerRates?.find(r => r?.lawyer_id === user?.id)?.base_hourly_rate || 0}
          onSave={handleCreateOverride}
          onClose={() => setShowOverrideModal(false)}
        />
      )}
    </div>
  );
};

export default LawyerRateManagement;
